import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import Sidebar from '../admin-dashboard/components/Sidebar';
import AdminBottomNav from '../admin-dashboard/components/AdminBottomNav';
import AdminMobileHeader from '../admin-dashboard/components/AdminMobileHeader';
import NewNotificationModal from '../notifications-management/components/NewNotificationModal';
import RulesTable from '../notifications-management/components/RulesTable';
import RuleModal from '../notifications-management/components/RuleModal';

const NotificationsRules = () => {
  const { t } = useTranslation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  // Managers (for manual notification)
  const [managers, setManagers] = useState([]);

  // Rules
  const [rules, setRules] = useState([]);
  const [rulesLoading, setRulesLoading] = useState(true);
  const [editingRule, setEditingRule] = useState(null);
  const [showRuleModal, setShowRuleModal] = useState(false);

  // Manual notification
  const [showNewNotif, setShowNewNotif] = useState(false);

  useEffect(() => {
    supabase?.auth?.getUser()?.then(({ data }) => setUserEmail(data?.user?.email || ''));
  }, []);

  const fetchManagers = useCallback(async () => {
    const { data } = await supabase?.from('users')?.select('id, name, email')?.in('role', ['manager', 'region_manager']);
    setManagers(data || []);
  }, []);

  const fetchRules = useCallback(async () => {
    setRulesLoading(true);
    const { data } = await supabase?.from('notification_rules')?.select('*')?.order('created_at', { ascending: false });
    setRules(data || []);
    setRulesLoading(false);
  }, []);

  useEffect(() => { fetchManagers(); fetchRules(); }, [fetchManagers, fetchRules]);

  const handleRuleToggle = (id, active) => {
    setRules(prev => prev?.map(r => r?.id === id ? { ...r, active } : r));
  };

  const handleRuleDelete = (id) => {
    setRules(prev => prev?.filter(r => r?.id !== id));
  };

  return (
    <div className="flex h-[100dvh] overflow-hidden" style={{ backgroundColor: 'var(--color-background)' }}>
      <div className="hidden md:flex">
        <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(v => !v)} userEmail={userEmail} />
      </div>
      <main className="flex-1 overflow-y-auto pb-16 md:pb-0">
        <AdminMobileHeader title={t('admin.notificationsRules.title', 'Envoi & Règles')} />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 flex flex-col gap-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
                {t('admin.notificationsRules.title', 'Envoi & Règles')}
              </h1>
              <p className="text-white/40 text-sm mt-1" style={{ fontFamily: 'Inter, sans-serif' }}>
                {t('admin.notificationsRules.subtitle', 'Envoyez des notifications manuelles et gérez les règles automatiques.')}
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setShowNewNotif(true)}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all"
                style={{ backgroundColor: 'var(--color-primary)', border: '1px solid rgba(229,32,42,0.3)' }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#cc0000'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = '#E5202A'}
              >
                {t('admin.notificationsRules.sendNotification', '+ Envoyer notification')}
              </button>
              <button
                onClick={() => { setEditingRule(null); setShowRuleModal(true); }}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
                style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.7)', border: '1px solid #1f1f1f' }}
              >
                {t('admin.notificationsRules.createRule', '+ Créer une règle')}
              </button>
            </div>
          </div>

          {/* Rules */}
          <div>
            <p className="text-white/50 text-sm mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              {rules?.length} règle{rules?.length !== 1 ? 's' : ''} configurée{rules?.length !== 1 ? 's' : ''}
            </p>

            {rulesLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-6 h-6 rounded-full border-2 border-red-500 border-t-transparent animate-spin" />
              </div>
            ) : (
              <RulesTable
                rules={rules}
                onEdit={(rule) => { setEditingRule(rule); setShowRuleModal(true); }}
                onDelete={handleRuleDelete}
                onToggle={handleRuleToggle}
              />
            )}
          </div>
        </div>
      </main>

      {showNewNotif && (
        <NewNotificationModal
          managers={managers}
          onClose={() => setShowNewNotif(false)}
          onCreated={() => { setShowNewNotif(false); }}
        />
      )}
      {showRuleModal && (
        <RuleModal
          rule={editingRule}
          onClose={() => { setShowRuleModal(false); setEditingRule(null); }}
          onSaved={() => fetchRules()}
        />
      )}
      <AdminBottomNav />
    </div>
  );
};

export default NotificationsRules;
